import type {
  Lang,
  PipeType,
  TextI18n,
} from './types';
import { shownName } from './names';

/** The pipes and ducts the app starts with: served alongside the Piping table's
 *  rows (a row with the same id is that type edited). */
export const DEFAULT_PIPE_TYPES: PipeType[] = [
  {
    id: 'copper-28', name: 'Copper pipe 28 mm', nameI18n: { it: 'Tubo in rame 28 mm', fr: 'Tube cuivre 28 mm' },
    material: 'Copper', discipline: 'plumbing', inner: 26, wall: 1, bendRadius: 28, slope: 5,
  },
  {
    id: 'copper-42', name: 'Copper pipe 42 mm', nameI18n: { it: 'Tubo in rame 42 mm', fr: 'Tube cuivre 42 mm' },
    material: 'Copper', discipline: 'plumbing', inner: 39.6, wall: 1.2, bendRadius: 42, slope: 5,
  },
  {
    id: 'drain-40', name: 'Drain pipe 40 mm', nameI18n: { it: 'Tubo di scarico 40 mm', fr: 'Tuyau d’évacuation 40 mm' },
    material: 'PP', discipline: 'drainage', inner: 36.4, wall: 1.8, bendRadius: 40, slope: 1,
  },
  {
    id: 'duct-100', name: 'Air duct 100 mm', nameI18n: { it: 'Condotto aria 100 mm', fr: 'Gaine d’air 100 mm' },
    material: 'Polypropylene', discipline: 'mechanical', inner: 100, wall: 5, bendRadius: 100, slope: 5,
  },
  {
    id: 'hose-6', name: 'Flexible hose 6 mm', nameI18n: { it: 'Tubo flessibile 6 mm', fr: 'Tuyau flexible 6 mm' },
    material: 'PE', discipline: 'plumbing', inner: 4, wall: 1, bendRadius: 0, slope: 0, flexible: true,
  },
];

/** A pipe's outer diameter. */
export function pipeOuter(p: { inner: number; wall: number }): number {
  return p.inner + 2 * p.wall;
}

/** A built-in type's default names (EN / IT / FR): what it is called while no
 *  name is entered for it. A type the user made has none. */
export function pipeTypeDefaults(id: string): TextI18n | null {
  const b = DEFAULT_PIPE_TYPES.find((t) => t.id === id);
  return b ? { en: b.name, ...b.nameI18n } : null;
}

/** The name entered for a type: '' while a built-in keeps its default. */
export function pipeTypeEntered(t: Pick<PipeType, 'id' | 'name'>): string {
  const d = pipeTypeDefaults(t.id);
  return d && t.name === d.en ? '' : t.name;
}

/** Name `t` as entered: a name shows as typed in every language (it has no
 *  translations); an empty one gives a built-in its default names back. */
export function setPipeTypeName(t: PipeType, entered: string): void {
  const d = pipeTypeDefaults(t.id), name = entered.trim();
  if (!name && d) {
    const b = DEFAULT_PIPE_TYPES.find((x) => x.id === t.id)!;
    t.name = b.name;
    if (b.nameI18n) t.nameI18n = { ...b.nameI18n }; else delete t.nameI18n;
    return;
  }
  if (name === t.name) return;
  t.name = name;
  delete t.nameI18n;
}

/** A type's name in `lang`: the one entered for it, else its default there. */
export function pipeTypeName(t: Pick<PipeType, 'id' | 'name' | 'nameI18n'>, lang: Lang): string {
  const entered = pipeTypeEntered(t);
  return shownName(entered, entered ? undefined : { en: t.name, ...t.nameI18n }, lang, t.name);
}

/** A type's Piping-table `data` blob. */
export function pipeTypeBlob(t: PipeType): Record<string, unknown> {
  const { rowId: _rowId, ...rest } = t;
  return { kind: 'pipe', ...rest };
}

const num = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

/** A parsed `data` blob back into a type, or null when it is not one. Missing
 *  figures fall back to the built-in of the same id, else to a 20 mm pipe. */
export function pipeTypeFromBlob(parsed: unknown, rowId?: number): PipeType | null {
  const o = parsed as Partial<Record<keyof PipeType, unknown>> & { kind?: unknown } | null;
  if (!o || o.kind !== 'pipe' || !o.id) return null;
  const id = String(o.id);
  const base = DEFAULT_PIPE_TYPES.find((t) => t.id === id);
  const discipline = ['electrical', 'plumbing', 'mechanical', 'drainage'].includes(String(o.discipline))
    ? (o.discipline as PipeType['discipline']) : base?.discipline ?? 'plumbing';
  const t: PipeType = {
    id,
    name: String(o.name ?? base?.name ?? id),
    material: String(o.material ?? base?.material ?? ''),
    discipline,
    inner: Math.max(0.1, num(o.inner, base?.inner ?? 20)),
    wall: num(o.wall, base?.wall ?? 1),
    bendRadius: num(o.bendRadius, base?.bendRadius ?? 20),
    slope: num(o.slope, base?.slope ?? 0),
  };
  if (o.flexible === true || (o.flexible === undefined && base?.flexible)) t.flexible = true;
  if (o.nameI18n && typeof o.nameI18n === 'object') t.nameI18n = { ...(o.nameI18n as PipeType['nameI18n']) };
  else if (base?.nameI18n && t.name === base.name) t.nameI18n = { ...base.nameI18n };
  if (rowId != null && Number.isFinite(rowId)) t.rowId = rowId;
  return t;
}

/** The catalogue: the built-ins, each replaced by the table's row of the same id,
 *  then the table's own types — so editing a built-in never loses the others. */
export function mergePipeTypes(rows: readonly PipeType[]): PipeType[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const out = DEFAULT_PIPE_TYPES.map((t) => byId.get(t.id) ?? { ...t, nameI18n: t.nameI18n && { ...t.nameI18n } });
  for (const r of rows) if (!DEFAULT_PIPE_TYPES.some((t) => t.id === r.id)) out.push(r);
  return out;
}

/** Whether `id` names a built-in (which can be edited, but not deleted). */
export function isBuiltinPipeType(id: string): boolean {
  return DEFAULT_PIPE_TYPES.some((t) => t.id === id);
}
